import React, {
    type ChangeEvent,
    type ClipboardEventHandler,
    type ElementType,
    type FocusEvent,
    type KeyboardEvent,
    type MouseEvent,
} from 'react';
import {type Dispatch} from 'redux';
import {FormattedMessage} from 'react-intl';

import {type Channel} from 'mattermost-redux/types/channels';
import {type ActionResult, type GenericAction} from 'mattermost-redux/types/actions';
import {type Group} from 'mattermost-redux/types/groups';
import {type GlobalState} from 'types/store';

import AutosizeTextarea from 'components/autosize_textarea';
import PostMarkdown from 'components/post_markdown';
import type Provider from 'components/suggestion/provider';
import AtMentionProvider from 'components/suggestion/at_mention_provider';
import ChannelMentionProvider from 'components/suggestion/channel_mention_provider';
import AppCommandProvider from 'components/suggestion/command_provider/app_provider';
import CommandProvider from 'components/suggestion/command_provider/command_provider';
import EmoticonProvider from 'components/suggestion/emoticon_provider';
import SuggestionBox from 'components/suggestion/suggestion_box';
import type SuggestionBoxComponent from 'components/suggestion/suggestion_box/suggestion_box';
import SuggestionList from 'components/suggestion/suggestion_list';

import * as Utils from 'utils/utils';
import {Utils as EditorUtils} from 'packages/editor';
import {validateHashtagsLength} from 'utils/text_formatting';
import {General} from 'mattermost-redux/constants';

type Props = {
    id: string;
    channelId: string;
    rootId?: string;
    tabIndex?: number;
    value: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onKeyPress: (e: KeyboardEvent) => void;
    onComposition?: () => void;
    onHeightChange?: (height: number, maxHeight: number) => void;
    createMessage: string;
    onKeyDown?: (e: KeyboardEvent) => void;
    onSelect?: (e: React.SyntheticEvent) => void;
    onMouseUp?: (e: React.MouseEvent) => void;
    onKeyUp?: (e: React.KeyboardEvent) => void;
    onBlur?: (e: FocusEvent) => void;
    supportsCommands: boolean;
    handlePostError?: (message: React.ReactNode | undefined) => void;
    onPaste?: ClipboardEventHandler;
    suggestionList?: React.ComponentProps<typeof SuggestionBox>['listComponent'];
    suggestionListPosition?: React.ComponentProps<typeof SuggestionList>['position'];
    emojiEnabled?: boolean;
    isRHS?: boolean;
    characterLimit: number;
    disabled?: boolean;
    badConnection?: boolean;
    listenForMentionKeyClick?: boolean;
    currentUserId: string;
    currentTeamId: string;
    withinDirect: boolean;
    preview?: boolean;
    actions: {
        autocompleteUsersInChannel: (
            prefix: string,
            channelId: string | undefined,
            params?: Record<string, unknown>,
        ) => (dispatch: Dispatch<GenericAction>, getState: GlobalState) => Promise<string[]>;
        autocompleteChannels: (
            term: string,
            success: (channels: Channel[]) => void,
            error: () => void,
            params?: Record<string, unknown>,
        ) => (dispatch: Dispatch<GenericAction>, getState: GlobalState) => Promise<ActionResult>;
        searchAssociatedGroupsForReference: (
            prefix: string,
            teamId: string,
            channelId: string | undefined,
        ) => (dispatch: Dispatch<GenericAction>, getState: GlobalState) => Promise<{ data: Group[] }>;
    };
    useChannelMentions: boolean;
    useGroupMentions: boolean;
    inputComponent?: ElementType;
    openWhenEmpty?: boolean;
};

export default class Textbox extends React.PureComponent<Props> {
    private suggestionProviders: Provider[];
    private wrapper: React.RefObject<HTMLDivElement>;
    private message: React.RefObject<SuggestionBoxComponent>;
    private preview: React.RefObject<HTMLDivElement>;

    static defaultProps = {
        supportsCommands: true,
        isRHS: false,
        listenForMentionKeyClick: false,
        inputComponent: AutosizeTextarea,
        suggestionList: SuggestionList,
    };

    constructor(props: Props) {
        super(props);

        this.suggestionProviders = [];

        if (props.supportsCommands) {
            this.suggestionProviders.push(new AppCommandProvider({
                teamId: this.props.currentTeamId,
                channelId: this.props.channelId,
                rootId: this.props.rootId,
            }));
        }

        this.suggestionProviders.push(
            new AtMentionProvider(
                {
                    currentUserId: this.props.currentUserId,
                    channelId: this.props.channelId,
                    autocompleteUsersInChannel: (prefix: string) =>
                        this.props.actions.autocompleteUsersInChannel(
                            prefix,
                            this.props.channelId,
                            {limit: General.AUTOCOMPLETE_LIMIT_DEFAULT},
                        ),
                    useChannelMentions: this.props.useChannelMentions,
                    useGroupMentions: this.props.useGroupMentions,
                    searchAssociatedGroupsForReference: (prefix: string) => this.props.actions.searchAssociatedGroupsForReference(prefix, this.props.currentTeamId, this.props.channelId),
                    withinThread: this.props.isRHS,
                    withinDirect: this.props.withinDirect,
                },
            ),
            new ChannelMentionProvider(
                (term: string, success: (...args: unknown[]) => void, error: (...args: unknown[]) => void) =>
                    props.actions.autocompleteChannels(term, success, error, {boost: true}),
                this.props.isRHS,
            ),
            new EmoticonProvider(this.props.isRHS),
        );

        if (props.supportsCommands) {
            this.suggestionProviders.push(new CommandProvider({
                teamId: this.props.currentTeamId,
                channelId: this.props.channelId,
                rootId: this.props.rootId,
                withinThread: this.props.isRHS,
            }));
        }

        this.validateMessage();
        this.wrapper = React.createRef();
        this.message = React.createRef();
        this.preview = React.createRef();
    }

    handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.props.onChange(e);
    }

    updateSuggestions(prevProps: Props) {
        if (this.props.channelId !== prevProps.channelId ||
            this.props.currentUserId !== prevProps.currentUserId ||
            this.props.useChannelMentions !== prevProps.useChannelMentions ||
            this.props.currentTeamId !== prevProps.currentTeamId) {
            // Update channel id for AtMentionProvider.
            for (const provider of this.suggestionProviders) {
                if (provider instanceof AtMentionProvider) {
                    provider.setProps({
                        currentUserId: this.props.currentUserId,
                        channelId: this.props.channelId,
                        autocompleteUsersInChannel: (prefix: string) =>
                            this.props.actions.autocompleteUsersInChannel(prefix, this.props.channelId,
                                {limit: General.AUTOCOMPLETE_LIMIT_DEFAULT},
                            ),
                        useChannelMentions: this.props.useChannelMentions,
                        useGroupMentions: this.props.useGroupMentions,
                        searchAssociatedGroupsForReference: (prefix: string) =>
                            this.props.actions.searchAssociatedGroupsForReference(
                                prefix,
                                this.props.currentTeamId,
                                this.props.channelId,
                            ),
                        withinThread: this.props.isRHS,
                        withinDirect: this.props.withinDirect,
                    });
                }
            }
        }

        if (this.props.channelId !== prevProps.channelId ||
            this.props.currentTeamId !== prevProps.currentTeamId ||
            this.props.rootId !== prevProps.rootId) {
            // Update channel id for CommandProvider and AppCommandProvider.
            for (const provider of this.suggestionProviders) {
                if (provider instanceof CommandProvider) {
                    provider.setProps({
                        teamId: this.props.currentTeamId,
                        channelId: this.props.channelId,
                        rootId: this.props.rootId,
                    });
                }
                if (provider instanceof AppCommandProvider) {
                    provider.setProps({
                        teamId: this.props.currentTeamId,
                        channelId: this.props.channelId,
                        rootId: this.props.rootId,
                    });
                }
            }
        }

        if (prevProps.value !== this.props.value) {
            this.validateMessage();
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (!prevProps.preview && this.props.preview) {
            this.preview.current?.focus();
        }

        this.updateSuggestions(prevProps);
    }

    validateMessage = () => {
        if (!this.props.handlePostError) {
            return;
        }

        if (!validateHashtagsLength(this.props.value)) {
            const errorMessage = (
                <FormattedMessage
                    id='editor.errors.hashtag_limit'
                    defaultMessage='You can add up to 1 000 symbols to hashtags'
                />);
            this.props.handlePostError(errorMessage);
            return;
        }

        if (this.props.value?.length > this.props.characterLimit) {
            const current = EditorUtils.formatLimitCounter(this.props.value.length);
            const max = EditorUtils.formatLimitCounter(this.props.characterLimit);
            const error = `${current}/${max}`;
            this.props.handlePostError(error);
            return;
        }

        this.props.handlePostError(undefined);
    }

    handleKeyDown = (e: KeyboardEvent) => {
        this.props.onKeyDown?.(e);
    }

    handleSelect = (e: React.SyntheticEvent) => {
        this.props.onSelect?.(e);
    }

    handleMouseUp = (e: MouseEvent) => {
        this.props.onMouseUp?.(e);
    }

    handleKeyUp = (e: KeyboardEvent) => {
        this.props.onKeyUp?.(e);
    }

    handleBlur = (e: FocusEvent) => {
        this.props.onBlur?.(e);
    }

    handleHeightChange = (height: number, maxHeight: number) => {
        this.props.onHeightChange?.(height, maxHeight);
    }

    getInputBox = () => {
        return this.message.current?.getTextbox();
    }

    focus = () => {
        const textbox = this.getInputBox();
        if (textbox) {
            textbox.focus();
            Utils.placeCaretAtEnd(textbox);

            // reset character count warning
            this.validateMessage();
        }
    }

    blur = () => {
        this.getInputBox()?.blur();
    };

    render() {
        let preview = null;

        let textboxClassName = 'form-control custom-textarea';
        let textWrapperClass = 'textarea-wrapper';
        if (this.props.emojiEnabled) {
            textboxClassName += ' custom-textarea--emoji-picker';
        }
        if (this.props.badConnection) {
            textboxClassName += ' bad-connection';
        }
        if (this.props.preview) {
            textboxClassName += ' custom-textarea--preview';
            textWrapperClass += ' textarea-wrapper--preview';

            preview = (
                <div
                    tabIndex={this.props.tabIndex || 0}
                    ref={this.preview}
                    className='form-control custom-textarea textbox-preview-area'
                    onKeyPress={this.props.onKeyPress}
                    onKeyDown={this.handleKeyDown}
                    onSelect={this.handleSelect}
                    onBlur={this.handleBlur}
                    data-qa='create_post_preview'
                >
                    <PostMarkdown
                        isRHS={this.props.isRHS}
                        message={this.props.value}
                        mentionKeys={[]}
                        channelId={this.props.channelId}
                    />
                </div>
            );
        }

        return (
            <div
                ref={this.wrapper}
                className={textWrapperClass}
            >
                <SuggestionBox
                    id={this.props.id}
                    ref={this.message}
                    className={textboxClassName}
                    spellCheck='true'
                    placeholder={this.props.createMessage}
                    onChange={this.handleChange}
                    onKeyPress={this.props.onKeyPress}
                    onSelect={this.handleSelect}
                    onKeyDown={this.handleKeyDown}
                    onMouseUp={this.handleMouseUp}
                    onKeyUp={this.handleKeyUp}
                    onComposition={this.props.onComposition}
                    onBlur={this.handleBlur}
                    onHeightChange={this.handleHeightChange}
                    onPaste={this.props.onPaste}
                    style={{visibility: this.props.preview ? 'hidden' : 'visible'}}
                    inputComponent={this.props.inputComponent}
                    listComponent={this.props.suggestionList}
                    listPosition={this.props.suggestionListPosition}
                    providers={this.suggestionProviders}
                    channelId={this.props.channelId}
                    value={this.props.value}
                    renderDividers={true}
                    isRHS={this.props.isRHS}
                    disabled={this.props.disabled}
                    contextId={this.props.channelId}
                    listenForMentionKeyClick={this.props.listenForMentionKeyClick}
                    openWhenEmpty={this.props.openWhenEmpty}
                />
                {preview}
            </div>
        );
    }
}
